import type { VariantWithStockDTO } from "@/modules/inventory/domain/contracts";
import { getStockStatus } from "@/modules/inventory/domain/contracts";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  Checkbox,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import type { RowSelectionState } from "@tanstack/react-table";
import { PackageIcon } from "lucide-react";
import type { ChangeEvent } from "react";

type StockTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: VariantWithStockDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  enableRowSelection?: boolean;
  onAdjustStock: (row: VariantWithStockDTO) => void;
  onViewMovements: (row: VariantWithStockDTO) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

const STATUS_LABELS: Record<string, string> = {
  out_of_stock: "ໝົດສະຕ໋ອກ",
  low_stock: "ສະຕ໋ອກຕ່ຳ",
  ok: "ສະຕ໋ອກປົກກະຕິ",
};

const STATUS_BADGE_VARIANTS: Record<
  string,
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "muted"
  | "success"
  | "warning"
> = {
  out_of_stock: "destructive",
  low_stock: "warning",
  ok: "success",
};

function stockStatusBadge(quantity: number, threshold: number | null) {
  const status = getStockStatus(quantity, threshold);
  return (
    <Badge variant={STATUS_BADGE_VARIANTS[status] ?? "default"}>
      {STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

function getVariantOptionLabel(
  optionValuesDetails: Array<{
    optionName: { lo?: string; th?: string; en?: string } | null;
    valueName: { lo?: string; th?: string; en?: string } | null;
  }> | null,
): string {
  if (!optionValuesDetails || optionValuesDetails.length === 0) return "";
  return optionValuesDetails
    .map((opt) => opt.valueName?.lo || opt.valueName?.en || "")
    .filter(Boolean)
    .join(", ");
}

export function StockTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onRowSelectionChange,
  enableRowSelection,
  onAdjustStock,
  onViewMovements: _onViewMovements,
  onPaginationChange,
  onSortingChange,
}: StockTableProps) {
  const columns: TanstackReactTable.ColumnDef<VariantWithStockDTO>[] = [
    ...(enableRowSelection
      ? [
          {
            id: "select",
            header: ({ table }) => (
              <Checkbox
                checked={
                  table.getIsAllPageRowsSelected()
                    ? true
                    : table.getIsSomePageRowsSelected()
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={(checked) => {
                  const ev = {
                    target: { checked: !!checked },
                  } as ChangeEvent<HTMLInputElement>;
                  table.getToggleAllPageRowsSelectedHandler()(ev);
                }}
                aria-label="ເລືອກທັງໝົດ"
              />
            ),
            cell: ({ row }) => (
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => {
                  const ev = {
                    target: { checked: value === true },
                  } as ChangeEvent<HTMLInputElement>;
                  row.getToggleSelectedHandler()(ev);
                }}
                aria-label="ເລືອກແຖວ"
              />
            ),
            enableSorting: false,
            size: 40,
          } as TanstackReactTable.ColumnDef<VariantWithStockDTO>,
        ]
      : []),
    createSortableColumn<VariantWithStockDTO>("sku", "ສິນຄ້າ", {
      size: 300,
      cell: ({ row }) => (
        <div className="flex items-start gap-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border bg-muted">
            <span className="text-muted-foreground text-xs">
              {row.original.product.name?.lo?.substring(0, 2) || "PR"}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-sm">
              {row.original.product.name?.lo || "—"}
            </p>
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <span className="font-mono">{row.original.sku}</span>
              {row.original.optionValuesDetails &&
                row.original.optionValuesDetails.length > 0 && (
                  <>
                    <span>•</span>
                    <span>
                      {getVariantOptionLabel(row.original.optionValuesDetails)}
                    </span>
                  </>
                )}
            </div>
          </div>
        </div>
      ),
    }),
    createSortableColumn<VariantWithStockDTO>("quantity", "ສະຕ໋ອກ", {
      size: 100,
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">{row.original.quantity}</span>
          {row.original.lowStockThreshold !== null && (
            <span className="text-muted-foreground text-xs">
              ເກນແຈ້ງເຕືອນ: {row.original.lowStockThreshold}
            </span>
          )}
        </div>
      ),
    }),
    createSortableColumn<VariantWithStockDTO>(
      "lowStockThreshold",
      "ເກນແຈ້ງເຕືອນ",
      {
        size: 100,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.lowStockThreshold !== null
              ? row.original.lowStockThreshold
              : "—"}
          </span>
        ),
      },
    ),
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        stockStatusBadge(row.original.quantity, row.original.lowStockThreshold),
      size: 120,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const actions = [
          // {
          //   label: "ເບິ່ງການເຄື່ອນໄຫວ",
          //   icon: <HistoryIcon className="h-4 w-4" />,
          //   onClick: () => onViewMovements(row.original),
          // },
          ...(canManage
            ? [
                {
                  label: "ປັບສະຕ໋ອກ",
                  icon: <PackageIcon className="h-4 w-4" />,
                  onClick: () => onAdjustStock(row.original),
                },
              ]
            : []),
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 140,
      enableSorting: false,
    },
  ];

  return (
    <div className="w-full" data-tourid="inventory-stock-table">
      <DataTable<VariantWithStockDTO, unknown>
        noDataMessage="ບໍ່ພົບສະຕ໋ອກ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(s) => {
          if (s[0]?.id === "") return;
          onSortingChange(s[0]?.id as string, !!s[0]?.desc);
        }}
        onRowSelectionChange={onRowSelectionChange}
        enableRowSelection={enableRowSelection}
      />
    </div>
  );
}
